import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { UserService } from 'app/core/user/user.service';
import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from 'app/shared/service/snackbar.service';

@Injectable()
export class AuthService {
    baseUrl: any = environment.baseURL;
    header: any;
    ssoHeader: any;
    private _authenticated: boolean = false;
    private justLoggedIn = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,private notification: MatSnackBar,
        private _userService: UserService, private translateService: TranslateService,
        private snackbarService: SnackbarService

    )
    {
        this.header = new HttpHeaders({
            "tier": localStorage.getItem('Tier'),
            'subdomain': localStorage.getItem('DomainName'),
            "apiKey": environment.apiKey
        });
    }

    setJustLoggedIn(value: boolean): void {
        this.justLoggedIn = value;
      }

      getJustLoggedIn(): boolean {
        return this.justLoggedIn;
      }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        const token = localStorage.getItem('token');
        const accessToken = localStorage.getItem('accessToken');
        return token !== null || accessToken !== null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }


    setHeaders(){
        this.header = new HttpHeaders({
            "tier": localStorage.getItem('Tier'),
            'subdomain': localStorage.getItem('DomainName'),
            "apiKey": environment.apiKey
        });
    }


    forgotPassword(userName: string): Observable<any> {
        this.setHeaders();
        return this._httpClient.put(`${this.baseUrl}resetPassword`, { userName }, {
            headers: this.header
        }).pipe(
            tap(() => {
                const message = this.translateService.instant('signInPage.PasswordResetLinkForgotPageLabel')
                this.snackbarService.showSuccessSnackbar({ message ,duration:2000});
            })
        );
    }


    loginUser(body: any): Observable<any> {
        this.setHeaders();
        return this._httpClient.post(this.baseUrl + 'sso/confirm', body, {
            headers: this.header
        });
    }


    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, resetToken: string, setPage:boolean): Observable<any> {
        this.setHeaders();
        return this._httpClient.put(`${this.baseUrl}confirmPassword`, { password, resetToken }, {
            headers: this.header
        }).pipe(
            tap(() => this.snackbarService.showSuccessSnackbar({ message: setPage ? this.translateService.instant('signInPage.passwordSetSuccessLabel') : this.translateService.instant('signInPage.passwordResetSuccessLabel'), duration: 2000 })
            ),
        );
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post('api/auth/sign-in', credentials).pipe(
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Renew token
        return this._httpClient.post('api/auth/refresh-access-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userNavigation');
        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/sign-up', user);
    }

}
